<template>
  <div>
    <header-admin :active-section="3" />

    <div class="header-row">
      <div class="panel-box filter-box">
        <label class="form-label">
          {{ trans("admin.draftExaminations.product") }}
        </label>

        <generic-admin-filter
          v-model="filters.product_id"
          class="filter-select"
          :placeholder="trans('admin.draftExaminations.product')"
          endpoint="products"
          label-field="display_name"
          value-field="id"
          @input="applyFilters"
        />

        <div class="metric-switch">
          <button
            type="button"
            class="btn btn-toggle"
            :class="{
              'btn-primary': activeMetric === 'CASES',
              'btn-outline-secondary': activeMetric !== 'CASES',
            }"
            @click="activeMetric = 'CASES'"
          >
            {{ trans("admin.analytics.examinations") }}
          </button>
          <button
            type="button"
            class="btn btn-toggle"
            :class="{
              'btn-primary': activeMetric === 'PATIENTS',
              'btn-outline-secondary': activeMetric !== 'PATIENTS',
            }"
            @click="activeMetric = 'PATIENTS'"
          >
            {{ trans("admin.analytics.patients") }}
          </button>
        </div>
      </div>

      <div class="panel-box kpi-box">
        <div class="kpi-title tooltip-container">
          <span v-if="totalsLoading">...</span>
          <span v-else>
            {{ formatNumber(displayedTotal) }}
          </span>
          <span class="tooltip-text">
            {{ trans("admin.analytics.explanation") }}
          </span>
        </div>
      </div>
    </div>

    <clip-loader v-show="loading" />
    <div v-for="bucket in collection" :key="bucket.year">
      <h3 class="my-3">
        {{ bucket.year }}
      </h3>
      <table class="table table-bordered my-2 table-actions-bar table-hover">
        <thead>
          <tr>
            <th>{{ trans("center.name") }}</th>
            <th>{{ trans("center.deletedAt.label") }}</th>
            <th v-for="month in months" :key="month">
              {{ month }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(center, index) in bucket.data" :key="index">
            <td>{{ center.centerName }}</td>
            <td>{{ center.deletedAt }}</td>
            <td v-for="monthEng in monthsEng" :key="monthEng">
              <b v-if="center[monthEng.toLowerCase()]">
                {{ formatCounters(center[monthEng.toLowerCase()]) }}
              </b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HeaderAdmin from "./Header.vue";
import GenericAdminFilter from "./Filters/GenericAdminFilter.vue";

export default {
  components: { HeaderAdmin, GenericAdminFilter },
  data() {
    return {
      loading: true,
      filters: { product_id: null },
      collection: [],
      totalsLoading: true,
      totals: { examinations: 0, patients: 0 },
      activeMetric: "CASES",
      displayedTotal: 0,
      animationFrame: null,
      months: moment
        .localeData(localStorage.getItem("lang_code", "en"))
        .monthsShort(),
      monthsEng: moment.localeData("en").monthsShort(),
    };
  },
  computed: {
    currentTotal() {
      return this.activeMetric === "CASES"
        ? this.totals.examinations
        : this.totals.patients;
    },
  },
  watch: {
    currentTotal: {
      immediate: true,
      handler(value) {
        this.animateTotal(value);
      },
    },
  },
  created() {
    this.fetchTotals();
    this.getAnalytics();
  },
  methods: {
    idOrUndefined(v) {
      if (v === null || v === undefined) return undefined;
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return undefined;
      return n;
    },
    formatNumber(value) {
      const n = Number(value || 0);
      if (!n) return "0";
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    formatCounters(month) {
      if (!month) return "";
      const key = this.activeMetric;
      const value = Number(month[key] ?? 0);
      if (!value) return "";
      return this.formatNumber(value);
    },
    animateTotal(target) {
      const start = this.displayedTotal || 0;
      if (start === target) return;
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      const change = target - start;
      const duration = 1200;
      const startTime = performance.now();
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.displayedTotal = Math.round(start + change * eased);
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(step);
        } else {
          this.animationFrame = null;
        }
      };
      this.animationFrame = requestAnimationFrame(step);
    },
    fetchTotals() {
      this.totalsLoading = true;
      axios
        .get("/api/internal/admin/analytics-total-examinations", {
          params: { product: this.idOrUndefined(this.filters.product_id) },
        })
        .then((res) => {
          this.totals = {
            examinations: Number(res.data.examinations || 0),
            patients: Number(res.data.patients || 0),
          };
          this.totalsLoading = false;
        })
        .catch(() => {
          this.totals = { examinations: 0, patients: 0 };
          this.totalsLoading = false;
        });
    },
    getAnalytics() {
      this.loading = true;
      axios
        .get("/api/internal/admin/analytics", {
          params: { product: this.idOrUndefined(this.filters.product_id) },
        })
        .then((res) => {
          this.collection = Object.values(res.data || {}).reverse();
          this.loading = false;
        })
        .catch(() => {
          this.collection = [];
          this.loading = false;
        });
    },
    applyFilters() {
      this.getAnalytics();
      this.fetchTotals();
    },
  },
};
</script>

<style scoped>
.header-row {
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex-wrap: wrap;
}

.panel-box {
  margin-top: 20px;
  width: 500px;
  min-height: 120px;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.filter-box {
  align-items: stretch;
}

.filter-select {
  width: 100%;
  margin-bottom: 0.75rem;
}

.kpi-box {
  justify-content: center;
  position: relative;
}

.kpi-title {
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.1;
  text-align: center;
  width: 100%;
  color: #333;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #f8f8f8;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.3;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease;
  pointer-events: none;
}

.tooltip-container:hover .tooltip-text {
  opacity: 1;
  visibility: visible;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
}

.btn {
  height: 38px;
  width: 100%;
}

.metric-switch {
  margin-top: 0.25rem;
  display: flex;
  gap: 0.5rem;
  width: 100%;
}

.btn-toggle {
  flex: 1 1 50%;
  height: 48px;
  font-size: 1.05rem;
}
</style>
